import { router } from 'expo-router';
import * as React from 'react';
import { useState } from 'react';
import {
    ArrowLeftRightIcon,
    Button,
    ButtonSize,
    ButtonVariant,
    FullScreenSpinner,
    isWeb,
    ScreenContainer,
    ScrollView,
    View,
    YStack,
} from 'smoothie';

import { isEmpty } from '../../../../../common/misc';
import useGetPaymentSchedules, {
    type PaymentScheduleRenderDetails,
} from '../../../../hooks/useGetPaymentSchedules';
import EmptyRecurringTransfers from './EmptyRecurringTransfers';
import RecurringTransferDetailsSheet from './RecurringTransferDetailsSheet';
import RecurringTransferLineItem from './RecurringTransferLineItem';

const ManageRecurringTransfers = () => {
    const {
        data: recurringTransfers,
        refetch,
        isLoading,
        isRefetching,
    } = useGetPaymentSchedules({});

    const [isDetailsSheetOpen, setIsDetailsSheetOpen] = useState(false);
    const [selectedTransfer, setSelectedTransfer] =
        useState<PaymentScheduleRenderDetails | null>(null);

    if (isLoading || isRefetching) {
        return <FullScreenSpinner />;
    }

    return (
        <>
            <ScreenContainer
                title="Manage recurring transfers"
                hasBackButton
                onBack={() => {
                    router.navigate('/home/banking');
                }}
            >
                <View>
                    {isEmpty(recurringTransfers) || !recurringTransfers ? (
                        <EmptyRecurringTransfers />
                    ) : (
                        <YStack
                            height="100%"
                            justifyContent={
                                isWeb ? 'flex-start' : 'space-between'
                            }
                            gap={32}
                        >
                            <ScrollView
                                showsVerticalScrollIndicator={isWeb}
                                $sm={{
                                    flex: 0,
                                    maxHeight: '80vh',
                                }}
                            >
                                <YStack marginTop={12}>
                                    {recurringTransfers
                                        // sort these schedules by:
                                        // 1. next execution date ASC
                                        // 2. amount DESC
                                        // 3. from account id ASC
                                        // 4. to account id ASC
                                        // 5. id ASC
                                        .sort(
                                            (a, b) =>
                                                (
                                                    a.nextPaymentDate
                                                        ?.execution_date ?? ''
                                                ).localeCompare(
                                                    b.nextPaymentDate
                                                        ?.execution_date ?? '',
                                                ) ||
                                                b.amount - a.amount ||
                                                a.fromAccount.id.localeCompare(
                                                    b.fromAccount.id,
                                                ) ||
                                                a.toAccount.id.localeCompare(
                                                    b.toAccount.id,
                                                ) ||
                                                a.id.localeCompare(b.id),
                                        )
                                        .map((transfer, i) => (
                                            <RecurringTransferLineItem
                                                key={transfer.id}
                                                isLast={
                                                    i ===
                                                    recurringTransfers.length -
                                                        1
                                                }
                                                transfer={transfer}
                                                onPress={() => {
                                                    setSelectedTransfer(
                                                        transfer,
                                                    );
                                                    setIsDetailsSheetOpen(true);
                                                }}
                                            />
                                        ))}
                                </YStack>
                            </ScrollView>
                            <Button
                                variant={ButtonVariant.PRIMARY}
                                size={ButtonSize.LARGE}
                                text="New transfer"
                                icon={ArrowLeftRightIcon}
                                onPress={() => {
                                    router.navigate('/banking/transfer');
                                }}
                            />
                        </YStack>
                    )}
                </View>
            </ScreenContainer>
            {selectedTransfer && isDetailsSheetOpen && (
                <RecurringTransferDetailsSheet
                    transfer={selectedTransfer}
                    refetchTransfers={refetch}
                    onClose={() => {
                        setIsDetailsSheetOpen(false);
                        setSelectedTransfer(null);
                    }}
                />
            )}
        </>
    );
};

export default ManageRecurringTransfers;
